import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import {getWindow} from 'ssr-window';
import AuthenticationAPI from 'api/authentication';
import ReferralAPI from 'api/referral';
import {getStore} from 'store/store';
import {userUpdate} from 'user/user-actions';
import UserUtils from 'user/user-utils';
import GTMUtils from 'utils/gtm';
import SegmentUtils from 'utils/segment';
import trackSignedUp from 'segment/events/signed-up';

const window = getWindow();
const FACEBOOK = 'Facebook';
const GOOGLE = 'Google';

const onHandleSuccess = (response, callback) => {
    if (response.data && response.data.data) {
        response.data = response.data.data;
    }

    if (isFunction(callback)) {
        callback(response);
    }
};

const onHandleFailure = (error, callback) => {
    let errors;

    if (error.data && error.data.data) {
        error.data = error.data.data;
    }

    if (isFunction(callback)) {
        try {
            errors = error.data.errors;
        } catch (e) {
            errors = false;
        }

        if (errors && error.status !== 500) {
            callback(errors);
        } else {
            callback([]);
        }
    }
};

const onAuthenticationSuccess = ({user, callback, socialAuthMethod = null}) => {
    if (!isEmpty(user)) {
        // Identify user before track call
        getStore().dispatch(
            userUpdate(
                UserUtils.normalize({
                    ...user,
                    status: UserUtils.AUTH_STATE.USER,
                })
            )
        );

        if (user.justCreated) {
            trackSignedUp({
                email: user.email,
                socialNetworkUsed: socialAuthMethod,
            });
        }
    }

    onHandleSuccess(user, callback);
};

const onAuthenticationFailure = ({
    error,
    callback,
    registration = false,
    socialAuthMethod = null,
}) => {
    SegmentUtils.track({
        event: 'Error Message Displayed',
        properties: {
            'error message': 'authentication error',
            'error action': `sign ${registration ? 'up' : 'in'}${
                socialAuthMethod ? `with ${socialAuthMethod}` : ''
            }`,
        },
    });

    onHandleFailure(error, callback);
};

const logInSocial = ({
    socialAuthProvider,
    accessToken,
    includes,
    email,
    registration,
    referralToken,
    referralSource,
    onSuccess,
    onFailure,
}) => {
    const loginMethod =
        socialAuthProvider === FACEBOOK
            ? AuthenticationAPI.loginFacebook
            : AuthenticationAPI.loginGoogle;
    const postData = {
        token: accessToken,
        mixpanel_id: SegmentUtils.getAnonymousId(), // eslint-disable-line camelcase
        include: UserUtils.INCLUDES,
    };

    if (includes) {
        postData.include += `,${includes}`;
    }

    if (referralToken) {
        postData.referral_token = referralToken; // eslint-disable-line camelcase
    }

    if (referralSource) {
        postData.referral_source = referralSource; // eslint-disable-line camelcase
    }

    if (email) {
        postData.email = email;
    }

    loginMethod(postData)
        .then(user => {
            onAuthenticationSuccess({
                user,
                callback: onSuccess,
                referralToken,
                socialAuthMethod: socialAuthProvider,
            });
        })
        .catch(error => {
            onAuthenticationFailure({
                error,
                registration,
                callback: onFailure,
                socialAuthMethod: socialAuthProvider,
            });
        });
};

const AuthenticationUtils = {
    logIn(username, password, remember, onSuccess, onFailure) {
        AuthenticationAPI.login({
            username,
            password,
            remember_me: Boolean(remember), // eslint-disable-line camelcase
            mixpanel_id: SegmentUtils.getAnonymousId(), // eslint-disable-line camelcase
            include: UserUtils.INCLUDES,
        })
            .then(user => {
                onAuthenticationSuccess({
                    user,
                    callback: onSuccess,
                });
            })
            .catch(error => {
                onAuthenticationFailure({
                    error,
                    callback: onFailure,
                });
            });
    },

    logInFacebook({
        includes,
        email,
        registration,
        referralToken,
        referralSource,
        onSuccess,
        onFailure,
    }) {
        if (isUndefined(window.FB)) {
            window.loadFacebookBackup(() => {
                AuthenticationUtils.logInFacebook({
                    includes,
                    email,
                    registration,
                    referralToken,
                    referralSource,
                    onSuccess,
                    onFailure,
                });
            });

            return;
        }

        window.FB.getLoginStatus(response => {
            if (response.status === 'connected') {
                // the user is logged in and has authenticated your
                // app, and response.authResponse supplies
                // the user's ID, a valid access token, a signed
                // request, and the time the access token
                // and signed request each expire
                logInSocial({
                    socialAuthProvider: FACEBOOK,
                    accessToken: response.authResponse.accessToken,
                    includes,
                    email,
                    registration,
                    referralToken,
                    referralSource,
                    onSuccess,
                    onFailure,
                });
            } else {
                // the user isn't logged in to Facebook.
                window.FB.login(
                    resp => {
                        if (resp.authResponse) {
                            // The person logged into your app
                            logInSocial({
                                socialAuthProvider: FACEBOOK,
                                accessToken: resp.authResponse.accessToken,
                                includes,
                                email,
                                registration,
                                referralToken,
                                referralSource,
                                onSuccess,
                                onFailure,
                            });
                        } else {
                            if (onFailure) {
                                onFailure([]);
                            }
                        }
                    },
                    {
                        scope: 'email',
                    }
                );
            }
        });
    },

    logInGoogle({
        includes,
        email,
        registration,
        referralToken,
        referralSource,
        onSuccess,
        onFailure,
    }) {
        if (isUndefined(window.gapi) || isUndefined(window.gapi.auth2)) {
            window.loadGoogleBackup(() => {
                AuthenticationUtils.logInGoogle({
                    includes,
                    email,
                    registration,
                    referralToken,
                    referralSource,
                    onSuccess,
                    onFailure,
                });
            });

            return;
        }

        window.gapi.auth2
            .getAuthInstance()
            .signIn()
            .then(
                response => {
                    logInSocial({
                        socialAuthProvider: GOOGLE,
                        accessToken: response.getAuthResponse(true)
                            .access_token,
                        includes,
                        email,
                        registration,
                        referralToken,
                        referralSource,
                        onSuccess,
                        onFailure,
                    });
                },
                err => {
                    if (onFailure) {
                        onFailure([]);
                    }
                }
            );
    },

    // spotheroReferral is an optional boolean to indicate this registration is part of a spothero promotion
    // when users register with invite code "spothero", promo code {{ window.shPromoCode.code }} will be applied at checkout
    // MP - 1/17/18: spotheroReferral was not used anywhere when porting this code from monolith so I removed
    // any references to it from inside this method
    register(
        email,
        password,
        referralToken,
        referralSource,
        spotheroReferral,
        onSuccess,
        onFailure,
        recaptchaResponse
    ) {
        AuthenticationAPI.register({
            email,
            password,
            mixpanel_id: SegmentUtils.getAnonymousId(), // eslint-disable-line camelcase
            referral_token: referralToken, // eslint-disable-line camelcase
            referral_source: referralSource, // eslint-disable-line camelcase
            recaptcha_response: recaptchaResponse, // eslint-disable-line camelcase
            include: UserUtils.INCLUDES,
        })
            .then(user => {
                onAuthenticationSuccess({
                    user,
                    callback: onSuccess,
                    wasReferred: !isNil(referralToken),
                });

                GTMUtils.push({
                    event: 'userRegistered',
                });
            })
            .catch(error => {
                onAuthenticationFailure({
                    error,
                    callback: onFailure,
                    registration: true,
                });
            });
    },

    resetPassword(email, onSuccess, onFailure) {
        AuthenticationAPI.resetPassword(email)
            .then(response => {
                onHandleSuccess(response, onSuccess);
            })
            .catch(error => {
                onHandleFailure(error, onFailure);
            });
    },

    checkReferralToken(token, onSuccess, onFailure) {
        ReferralAPI.checkReferralToken(token)
            .then(response => {
                onHandleSuccess(response, onSuccess);
            })
            .catch(error => {
                onHandleFailure(error, onFailure);
            });
    },

    changePassword(token, newPassword, onSuccess, onFailure) {
        AuthenticationAPI.setPassword(token, newPassword)
            .then(response => {
                onHandleSuccess(response, onSuccess);
            })
            .catch(error => {
                onHandleFailure(error, onFailure);
            });
    },
};

export default AuthenticationUtils;
